import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  FaUsers, FaCogs, FaChartLine, FaDatabase, FaCloud, FaRobot, FaMobileAlt, FaArrowRight, FaStar 
} from "react-icons/fa";
import { AiFillAppstore } from "react-icons/ai";
import SEO from "../layout/SEO";

const services = [
  {
    icon: <FaUsers />,
    title: "CRM",
    link: "/crm-feature-section",
    description: "Strategy, technology, and process for managing customer interactions, improving relationships and driving sales growth.",
    color: "from-blue-500 to-blue-700",
  },
  {
    icon: <FaCogs />,
    title: "ERP",
    link: "/erp-feature-section",
    description: "Software system to manage and integrate core business processes in a unified system for operational efficiency.",
    color: "from-green-500 to-green-700",
  },
  {
    icon: <FaChartLine />,
    title: "Sales Management",
    link: "/sales-management",
    description: "Track leads, monitor performance, and optimize your sales funnel for better revenue growth.",
    color: "from-purple-500 to-purple-700",
  },
  {
    icon: <FaDatabase />,
    title: "Payroll Management",
    link: "/payroll-management",
    description: "Administration of financial records for managing employee compensation, salaries, and tax withholdings.",
    color: "from-orange-500 to-orange-700",
  },
  {
    icon: <AiFillAppstore />,
    title: "Services Management",
    link: "/services-mnagement",
    description: "Comprehensive process of managing employee compensation including salaries, wages, bonuses, and deductions.",
    color: "from-red-500 to-red-700",
  },
  {
    icon: <FaCloud />,
    title: "AMC Management",
    link: "/cloud-solutions",
    description: "Annual Maintenance Contract to ensure your assets and systems are managed and maintained effectively.",
    color: "from-cyan-500 to-cyan-700",
  },
  {
    icon: <FaRobot />,
    title: "Inventory Management",
    link: "/inventory-management",
    description: "Efficient oversight of procurement, storage, and usage of materials or products in stock.",
    color: "from-pink-500 to-pink-700",
  },
  {
    icon: <FaMobileAlt />,
    title: "Accounting Management",
    link: "/account-management",
    description: "Systematic recording, measuring, analyzing, and communicating financial information about an organization.",
    color: "from-indigo-500 to-indigo-700",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { y: 40, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
};

const Serverices = () => {
  return (
    <section className="bg-gradient-to-br from-slate-50 via-white to-blue-50 py-20 md:py-28">
      <SEO
        title="Services - SAPEAGLE ERP"
        description="Explore SAPEAGLE ERP services - CRM, ERP, Sales, Payroll, and more."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6"
          >
            <FaStar className="text-amber-500" />
            <span className="text-blue-700 text-sm font-medium">Premium Solutions</span>
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Explore Our{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Services
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover our comprehensive suite of enterprise solutions designed to transform your business operations.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, index) => (
            <Link key={index} to={service.link}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group bg-white rounded-3xl shadow-md hover:shadow-2xl transition-all duration-300 p-6 flex flex-col h-full relative overflow-hidden"
              >
                {/* Gradient Background on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                
                {/* Icon */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-5 shadow-lg group-hover:shadow-xl transition-shadow duration-300`}
                >
                  <span className="text-white text-3xl">{service.icon}</span>
                </motion.div>

                {/* Content */}
                <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {service.title}
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-4">
                  {service.description}
                </p>

                {/* Learn More Link */}
                <div className="flex items-center gap-2 text-blue-600 font-semibold group-hover:gap-3 transition-all duration-300">
                  <span>Learn More</span>
                  <FaArrowRight className="w-4 h-4" />
                </div>

                {/* Decorative Corner */}
                <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-bl-full`} />
              </motion.div>
            </Link>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-10 md:p-14 shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Get started with our comprehensive ERP solutions and take your business to the next level.
            </p>
            <Link
              to="/book-demo-section"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-2xl font-bold hover:bg-gray-100 transition-colors duration-300 shadow-lg hover:shadow-xl"
            >
              Get a Free Demo
              <FaArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Serverices;
